import { z } from 'zod';
import {
  authenticatedSchema,
  otpChallengeSchema,
  type Authenticated,
  type OtpChallenge,
} from '../auth/auth-contract.js';
import type { ApiClient } from '../api/client.js';

/**
 * The three calls an invited person makes before they have an account.
 *
 * Every one of them sets `skipAuthRefresh`, exactly as the login endpoints do: there is no
 * session to refresh, and a 401 here must surface as itself rather than triggering a refresh
 * loop against a shop this person does not yet belong to.
 *
 * The invitation's details — which shop, what role — arrive with the VERIFY response and not
 * before, because the API will not say whether a number was invited until that number has
 * proved it is answering.
 */

export const invitePreviewSchema = z.object({
  shopName: z.string().nullable(),
  role: z.enum(['manager', 'tailor']),
  name: z.string(),
  /** True when this number already signs in somewhere, so no new credential is needed. */
  hasCredential: z.boolean(),
});
export type InvitePreview = z.infer<typeof invitePreviewSchema>;

const joinVerificationSchema = z.object({
  verificationToken: z.string(),
  expiresIn: z.number(),
  invite: invitePreviewSchema,
});
export type JoinVerification = z.infer<typeof joinVerificationSchema>;

export async function startJoin(client: ApiClient, email: string): Promise<OtpChallenge> {
  return await client.post(
    '/staff-join/start',
    { email },
    { schema: otpChallengeSchema, skipAuthRefresh: true },
  );
}

export async function verifyJoin(
  client: ApiClient,
  input: { email: string; code: string },
): Promise<JoinVerification> {
  return await client.post('/staff-join/verify', input, {
    schema: joinVerificationSchema,
    skipAuthRefresh: true,
  });
}

export async function completeJoin(
  client: ApiClient,
  input: { verificationToken: string; name: string; pin?: string; password?: string },
): Promise<Authenticated> {
  return await client.post('/staff-join/complete', input, {
    schema: authenticatedSchema,
    skipAuthRefresh: true,
  });
}
